import React, { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux'
import { PageTitle } from '@/components/custom/PageTitle'
import { PageDescription } from '@/components/custom/PageDescription'
import { getSubscription } from '@/services/billingService'

export const BillingSuccess: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const sessionId = searchParams.get('session_id')
  const dispatch: AppDispatch = useDispatch()
  const { token, userData } = useSelector((state: RootState) => state.user)
  const currentOrgId = userData?.currentOrganization?.id

  useEffect(() => {
    if (token && currentOrgId) {
      dispatch(getSubscription(token, currentOrgId))
    }
  }, [token, currentOrgId, dispatch])

  return (
    <div className="protectedPageLayout">
      <PageTitle title="Payment Successful" />
      <PageDescription
        title="Thank you for your purchase!"
        subtitle="Your payment has been processed successfully."
      />

      <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-green-800 mb-2">
          Payment Successful!
        </h2>
        <p className="text-green-700 mb-6">
          Your subscription has been activated and you now have access to all the features of your plan.
        </p>

        {sessionId && (
          <div className="bg-white border border-green-200 rounded-md p-4 mb-6">
            <p className="text-sm text-gray-600 mb-1">Transaction ID:</p>
            <p className="font-mono text-sm text-gray-800 break-all">{sessionId}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate('/billing')}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              Go to Billing Now
            </button>
            <button
              onClick={() => navigate('/workflows')}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Start Creating Workflows
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">What's Next?</h3>
        <ul className="text-blue-700 space-y-2">
          <li className="flex items-center">
            <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Set up your credentials to connect to your applications
          </li>
          <li className="flex items-center">
            <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Create your first automated workflow
          </li>
          <li className="flex items-center">
            <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Invite team members to collaborate
          </li>
        </ul>
      </div>

      <div className="mt-6 text-center text-sm text-gray-600">
        <p>
          Need help getting started? Visit our{' '}
          <button
            onClick={() => navigate('/support')}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Support page
          </button>{' '}
          for guides and documentation.
        </p>
      </div>
    </div>
  )
}
